import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useAppSelector } from "@/store/hooks"
import Money from "@/Components/Money"
import classNames from "classnames"
import { dividePriceIntoBills } from "@/utils/animationHelpers"

// Gap between the pile and the top edge of the tooltip card.
const GAP = 12
// Keep the card this far away from the viewport edges when clamping.
const EDGE_PADDING = 8

const MoneyOnTable: React.FC = () => {
  const moneyOnTable = useAppSelector(
    (state) => state.global.myGame?.situation?.moneyOnTable || 0
  )

  const pileRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  // Tapping pins the card open — hover alone is useless on touch screens.
  const [pinned, setPinned] = useState(false)
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)
  // Horizontal correction applied after measuring, so the card doesn't hang off
  // the screen on narrow layouts.
  const [offsetX, setOffsetX] = useState(0)

  const open = hovered || pinned

  // The card is portalled to the body: this pile lives inside #center, which
  // gets a brightness filter during some animations, and a filter both dims and
  // traps positioned descendants.
  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      if (!pileRef.current) return
      const rect = pileRef.current.getBoundingClientRect()
      setAnchor({ x: rect.left + rect.width / 2, y: rect.bottom + GAP })
    }
    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
    }
  }, [open])

  // Measure once positioned and nudge the card back inside the viewport. The
  // correction is derived from the card's own width rather than accumulated from
  // the previous offset, so it settles in one pass.
  useLayoutEffect(() => {
    if (!open || !anchor || !cardRef.current) {
      setOffsetX(0)
      return
    }
    // offsetWidth, not getBoundingClientRect: the latter is scaled down while
    // the entrance animation is still running.
    const width = cardRef.current.offsetWidth
    const desiredLeft = anchor.x - width / 2
    const clampedLeft = Math.max(
      EDGE_PADDING,
      Math.min(desiredLeft, window.innerWidth - width - EDGE_PADDING)
    )
    setOffsetX(clampedLeft - desiredLeft)
  }, [open, anchor])

  // A pinned card is dismissed by tapping/clicking anywhere else.
  useEffect(() => {
    if (!pinned) return
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null
      if (target && pileRef.current?.contains(target)) return
      setPinned(false)
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("touchstart", handlePointerDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
    }
  }, [pinned])

  if (moneyOnTable <= 0) return null

  // Get the bill items for the total amount
  const billItems = dividePriceIntoBills(moneyOnTable)
  const highestBill = billItems.find(
    (item) => item.value === Math.max(...billItems.map((item) => item.value))
  )

  return (
    <>
      <div
        ref={pileRef}
        data-testid="game.money-on-table"
        className="absolute top-0 right-left flex flex-col items-center gap-2 z-10 cursor-help"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => {
          e.stopPropagation()
          setPinned((prev) => !prev)
        }}
      >
        <img
          src={highestBill?.image}
          className={classNames(
            "h-16 w-auto shadow-lg transition-transform duration-200 ease-out",
            open ? "-translate-y-1 scale-110 -rotate-3" : ""
          )}
          style={{
            filter: open
              ? "drop-shadow(0 6px 12px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(245,158,11,0.65))"
              : "none",
          }}
          alt={`${highestBill?.value} bill`}
        />
        <Money
          money={moneyOnTable}
          className={classNames(
            "font-medium font-poppins gap-0.5 text-2xl transition-colors duration-200",
            open ? "text-amber-700" : "text-gray-600"
          )}
          iconClass="h-4"
        />
      </div>

      {open && anchor
        ? createPortal(
            <div
              ref={cardRef}
              // rem, like the text inside it, so the whole card scales together
              // with the root font size instead of the text outgrowing a fixed
              // pixel box on narrow/short screens.
              className="fixed z-[350] w-[16rem] max-w-[70vw] pointer-events-none money-table-tip"
              style={{ left: anchor.x + offsetX, top: anchor.y }}
            >
              <div
                className="relative rounded-xl border border-amber-400/40 px-3.5 py-3 font-poppins backdrop-blur-md"
                style={{
                  background:
                    "linear-gradient(150deg, rgba(24,24,27,0.97) 0%, rgba(9,9,11,0.97) 100%)",
                  boxShadow:
                    "0 12px 32px rgba(0,0,0,0.65), 0 0 18px rgba(245,158,11,0.25)",
                }}
              >
                {/* Arrow, kept level with the pile even after horizontal clamping. */}
                <div
                  className="absolute h-2.5 w-2.5 top-[-5px] border-l border-t border-amber-400/40"
                  style={{
                    left: "50%",
                    marginLeft: -offsetX,
                    transform: "translateX(-50%) rotate(45deg)",
                    background: "rgb(20,18,16)",
                  }}
                ></div>

                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-mask text-amber-400 text-[0.8125rem]"></i>
                  <span className="text-[1.0625rem] font-bold leading-none tracking-wide text-amber-400">
                    Money on the table
                  </span>
                </div>

                <div className="mt-2 text-[0.9375rem] leading-snug text-white/70">
                  Taxes and fines paid to the bank pile up here. The next player
                  to land on the <b>Steal Tax</b> tile takes all of it.
                </div>

                <div className="mt-2.5 flex items-center gap-1.5 rounded-md bg-amber-400/20 px-2 py-2 text-[0.9375rem] leading-snug font-medium text-amber-300">
                  <i className="fa-solid fa-bolt text-[0.75rem] shrink-0"></i>
                  <span className="flex items-center gap-1">
                    Up for grabs:
                    <Money
                      money={moneyOnTable}
                      className="gap-0.5 font-semibold"
                      iconClass="h-3.5"
                    />
                  </span>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  )
}

export default MoneyOnTable
